import React, { useState, useEffect } from 'react';
import { Ic, ICONS } from '../data/icons';
import {
  Card,
  Modal,
  Badge,
  FormField,
  Button,
  SearchInput,
  EmptyState,
  Pagination
} from '../components/shared';
import { fetchMdmApps } from '../api/client';
import '../assets/styles/pages.css';

const MdmAppsManagement = ({ C, currentUser }) => {
  const [apps, setApps] = useState([]);
  const [filteredApps, setFilteredApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOS, setFilterOS] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedApp, setSelectedApp] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    bundle_id: '',
    version: '',
    icon_url: '',
    description: '',
    category: 'Other',
    is_mandatory: false,
    target_os: 'iOS',
    download_url: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Load apps on mount
  useEffect(() => {
    loadApps();
  }, []);

  // Filter apps when search or OS filter changes
  useEffect(() => {
    let filtered = apps;

    if (filterOS !== 'all') {
      filtered = filtered.filter(app => app.target_os === filterOS);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(app =>
        app.name.toLowerCase().includes(term) ||
        app.bundle_id.toLowerCase().includes(term)
      );
    }

    setFilteredApps(filtered);
    setCurrentPage(1);
  }, [searchTerm, filterOS, apps]);

  const loadApps = async () => {
    try {
      setLoading(true);
      const response = await fetchMdmApps('iOS');
      // fetchMdmApps returns apps with status info, we need to extract actual app data
      // For now, use the response directly
      const appsList = Array.isArray(response) ? response : response.apps || [];
      setApps(appsList);
    } catch (err) {
      console.error('Failed to load apps:', err);
      // Demo mode - use mock data
      setApps(DEMO_APPS);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (mode = 'create', app = null) => {
    setModalMode(mode);
    if (mode === 'edit' && app) {
      setSelectedApp(app);
      setFormData({
        name: app.name,
        bundle_id: app.bundle_id,
        version: app.version,
        icon_url: app.icon_url || '',
        description: app.description || '',
        category: app.category || 'Other',
        is_mandatory: app.is_mandatory || false,
        target_os: app.target_os || 'iOS',
        download_url: app.download_url || ''
      });
    } else {
      setFormData({
        name: '',
        bundle_id: '',
        version: '',
        icon_url: '',
        description: '',
        category: 'Other',
        is_mandatory: false,
        target_os: 'iOS',
        download_url: ''
      });
    }
    setFormErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedApp(null);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Uygulama adı gerekli';
    if (!formData.bundle_id.trim()) errors.bundle_id = 'Bundle ID gerekli';
    if (!formData.version.trim()) errors.version = 'Versiyon gerekli';
    if (!formData.target_os) errors.target_os = 'İşletim sistemi gerekli';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveApp = async () => {
    if (!validateForm()) return;

    try {
      setIsSaving(true);
      const url = modalMode === 'create'
        ? `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/enrollment/mdm-apps`
        : `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/enrollment/mdm-apps/${selectedApp.id}`;

      const method = modalMode === 'create' ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Uygulama kaydedilemedi');
      }

      await loadApps();
      handleCloseModal();

      // Show success notification (optional - can implement toast later)
      console.log(modalMode === 'create' ? 'App created' : 'App updated');
    } catch (err) {
      console.error('Error saving app:', err);
      setFormErrors({ submit: err.message });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = (app) => {
    setDeleteTarget(app);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/enrollment/mdm-apps/${deleteTarget.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Uygulama silinemedi');
      }

      await loadApps();
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
    } catch (err) {
      console.error('Error deleting app:', err);
      alert('Silme işlemi başarısız: ' + err.message);
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Pagination
  const totalPages = Math.ceil(filteredApps.length / itemsPerPage);
  const startIdx = (currentPage - 1) * itemsPerPage;
  const paginatedApps = filteredApps.slice(startIdx, startIdx + itemsPerPage);

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>MDM Uygulamaları Yönetimi</h1>
        </div>
        <Card C={C} className="loading-state">
          <div className="spinner" />
          <p>Uygulamalar yükleniyor...</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1>MDM Uygulamaları Yönetimi</h1>
          <p>Cihazlara dağıtılacak uygulamaları yönetin</p>
        </div>
        <Button
          C={C}
          onClick={() => handleOpenModal('create')}
          variant="primary"
        >
          <Ic d={ICONS.plus} size={16} s='#fff' /> Yeni Uygulama
        </Button>
      </div>

      {/* Filters */}
      <Card C={C} className="filters-card">
        <div className="filters-row">
          <SearchInput
            C={C}
            placeholder="Uygulama adı veya Bundle ID ile ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <select
            value={filterOS}
            onChange={(e) => setFilterOS(e.target.value)}
            className="filter-select"
          >
            <option value="all">Tümü</option>
            <option value="iOS">iOS</option>
            <option value="iPadOS">iPadOS</option>
            <option value="macOS">macOS</option>
          </select>
        </div>
      </Card>

      {/* Apps Table */}
      {paginatedApps.length === 0 ? (
        <EmptyState
          C={C}
          title={apps.length === 0 ? "Uygulama bulunamadı" : "Arama sonucu yok"}
          desc={apps.length === 0 ? "Henüz bir uygulama eklenmemiş" : "Ararama kriterlerinize uygun uygulama yok"}
          action={apps.length === 0 ? () => handleOpenModal('create') : undefined}
          actionLabel="Yeni Uygulama Ekle"
        />
      ) : (
        <>
          <Card C={C} className="table-card">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Uygulama Adı</th>
                  <th>Bundle ID</th>
                  <th>Versiyon</th>
                  <th>Kategori</th>
                  <th>İşletim Sistemi</th>
                  <th>Zorunlu</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {paginatedApps.map((app) => (
                  <tr key={app.id}>
                    <td>
                      <div className="app-cell">
                        {app.icon_url && (
                          <img src={app.icon_url} alt={app.name} className="app-icon" />
                        )}
                        <span>{app.name}</span>
                      </div>
                    </td>
                    <td>
                      <code className="bundle-id">{app.bundle_id}</code>
                    </td>
                    <td>{app.version}</td>
                    <td>
                      <Badge color="#9333ea" small>
                        {app.category || 'Other'}
                      </Badge>
                    </td>
                    <td>
                      <Badge
                        color={
                          app.target_os === 'iOS' ? '#3b82f6' :
                          app.target_os === 'iPadOS' ? '#10b981' :
                          '#8b5cf6'
                        }
                        small
                      >
                        {app.target_os}
                      </Badge>
                    </td>
                    <td>
                      <Badge
                        color={app.is_mandatory ? '#ef4444' : '#94a3b8'}
                        small
                      >
                        {app.is_mandatory ? 'Evet' : 'Hayır'}
                      </Badge>
                    </td>
                    <td>
                      <div className="actions-cell">
                        <button
                          onClick={() => handleOpenModal('edit', app)}
                          className="action-btn edit"
                          title="Düzenle"
                        >
                          <Ic d={ICONS.edit} size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(app)}
                          className="action-btn delete"
                          title="Sil"
                        >
                          <Ic d={ICONS.trash} size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination
              C={C}
              page={currentPage}
              total={filteredApps.length}
              perPage={itemsPerPage}
              onChange={setCurrentPage}
            />
          )}
        </>
      )}

      {/* Modal - Create/Edit App */}
      <Modal
        C={C}
        isOpen={showModal}
        onClose={handleCloseModal}
        title={modalMode === 'create' ? 'Yeni Uygulama Ekle' : 'Uygulamayı Düzenle'}
      >
        <div className="modal-content">
          {formErrors.submit && (
            <div className="error-alert">
              <p>{formErrors.submit}</p>
            </div>
          )}

          <div className="form-row">
            <FormField
              C={C}
              label="Uygulama Adı *"
              error={formErrors.name}
            >
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                placeholder="Örn: Microsoft Outlook"
                className="form-input"
              />
            </FormField>

            <FormField
              C={C}
              label="Bundle ID *"
              error={formErrors.bundle_id}
            >
              <input
                type="text"
                name="bundle_id"
                value={formData.bundle_id}
                onChange={handleFormChange}
                placeholder="Örn: com.microsoft.onenote.iphone"
                className="form-input"
              />
            </FormField>
          </div>

          <div className="form-row">
            <FormField
              C={C}
              label="Versiyon *"
              error={formErrors.version}
            >
              <input
                type="text"
                name="version"
                value={formData.version}
                onChange={handleFormChange}
                placeholder="Örn: 4.85.0"
                className="form-input"
              />
            </FormField>

            <FormField
              C={C}
              label="Kategori"
            >
              <select
                name="category"
                value={formData.category}
                onChange={handleFormChange}
                className="form-input"
              >
                <option value="Productivity">İşletim</option>
                <option value="Communication">İletişim</option>
                <option value="Business">İşletme</option>
                <option value="Security">Güvenlik</option>
                <option value="Development">Geliştirme</option>
                <option value="Other">Diğer</option>
              </select>
            </FormField>
          </div>

          <FormField
            C={C}
            label="Açıklama"
          >
            <textarea
              name="description"
              value={formData.description}
              onChange={handleFormChange}
              placeholder="Uygulama hakkında açıklama yazın..."
              className="form-input"
              rows="3"
            />
          </FormField>

          <div className="form-row">
            <FormField
              C={C}
              label="İşletim Sistemi *"
              error={formErrors.target_os}
            >
              <select
                name="target_os"
                value={formData.target_os}
                onChange={handleFormChange}
                className="form-input"
              >
                <option value="iOS">iOS</option>
                <option value="iPadOS">iPadOS</option>
                <option value="macOS">macOS</option>
              </select>
            </FormField>

            <FormField C={C} label="İkon URL">
              <input
                type="url"
                name="icon_url"
                value={formData.icon_url}
                onChange={handleFormChange}
                placeholder="https://..."
                className="form-input"
              />
            </FormField>
          </div>

          <FormField C={C} label="İndirme URL'i">
            <input
              type="url"
              name="download_url"
              value={formData.download_url}
              onChange={handleFormChange}
              placeholder="https://..."
              className="form-input"
            />
          </FormField>

          <FormField C={C}>
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="is_mandatory"
                checked={formData.is_mandatory}
                onChange={handleFormChange}
              />
              <span>Bu uygulamayı zorunlu kıl (tüm cihazlara otomatik dağıt)</span>
            </label>
          </FormField>

          {/* Modal Actions */}
          <div className="modal-actions">
            <Button
              C={C}
              onClick={handleCloseModal}
              variant="secondary"
              disabled={isSaving}
            >
              İptal
            </Button>
            <Button
              C={C}
              onClick={handleSaveApp}
              variant="primary"
              disabled={isSaving}
            >
              {isSaving ? 'Kaydediliyor...' : modalMode === 'create' ? 'Ekle' : 'Güncelle'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        C={C}
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Uygulamayı Sil"
      >
        <div className="modal-content confirm">
          <p>
            <strong>{deleteTarget?.name}</strong> uygulamasını silmek istediğinize emin misiniz?
          </p>
          <p className="text-muted">Bu işlem geri alınamaz.</p>

          <div className="modal-actions">
            <Button
              C={C}
              onClick={() => setShowDeleteConfirm(false)}
              variant="secondary"
            >
              İptal
            </Button>
            <Button
              C={C}
              onClick={handleConfirmDelete}
              variant="danger"
            >
              Sil
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

// Demo data for offline testing
const DEMO_APPS = [
  {
    id: 1,
    name: 'Microsoft Outlook',
    bundle_id: 'com.microsoft.onenote.iphone',
    version: '4.85.0',
    icon_url: 'https://www.microsoft.com/favicon.ico',
    description: 'Email ve takvim yönetimi',
    category: 'Productivity',
    is_mandatory: true,
    target_os: 'iOS',
    download_url: 'https://apps.apple.com/app/microsoft-outlook/id951937694'
  },
  {
    id: 2,
    name: 'Microsoft Teams',
    bundle_id: 'com.microsoft.skype.teams',
    version: '5.45.0',
    icon_url: 'https://www.microsoft.com/favicon.ico',
    description: 'İşbirliği ve iletişim',
    category: 'Communication',
    is_mandatory: true,
    target_os: 'iOS',
    download_url: 'https://apps.apple.com/app/microsoft-teams/id1113038709'
  }
];

export default MdmAppsManagement;
